import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import LockIcon from '@upright/ui/icons/img/streamline-micro-line/interface-essential/lock-unlock/interface-lock.svg';
import * as React from 'react';
import { OptionProps } from 'react-select';
import styled from 'styled-components';
import { User } from '../../../../../../../../common/iam/users/types';
import { isProductLike } from '../../../../../../../../common/model/input';
import { ProductContext } from '../../../../../../../../common/model/input/companies/api-types';
import { ModelEntityReference, ModelEntityType } from '../../../../../../../../common/model/input/types';
import { ModelEntitySearchResponseItem } from '../../../../../../../../common/model/search/types';
import { getUiUrl, isTeamUser } from '../../../../../../../../common/utils';
import {
  NotCurrentlyAvailableText,
  RestrictedContentTextBrief,
  UpgradeSubscriptionTextBrief,
} from '../../../../../../../untidy/text-elements';
import {
  getCompanyGroupViewUrl,
  getCompanyViewUrl,
  getPortfolioViewUrl,
  getProductViewInternalUrl,
  getProductViewUrl,
} from '../../../../../../../untidy/urls';
import { InternalProductViewMode } from '../../../../../../../views/model-entity-views/product-view/internal/types';
import { InfoTooltip } from '../../../../../../generic/components/tooltips/info-tooltip';
import { useIsClientOrTeamOrFreeTrial, useIsStandardOrExtendedCoverageUser } from '../../../../../../generic/hooks';
import { getOptionIcon } from '../../option-icon';
import { SearchTheme } from '../../types';
import { CountryBlock } from './country-block';
import { DescriptionBlock } from './description-block';
import { DisambiguationBlock } from './disambiguation-block';
import { NameBlock } from './icon-name-block';
import { SpecialInformationBlock } from './special-info-block';

/**
 * Get a custom option
 * @param user user
 * @param creatable name of (suggested) creatable
 *   (when supporting creating new items through search)
 */

interface Params {
  headerSearch: boolean;
  theme: SearchTheme;
  user: User | undefined;
  creatable?: string;
  onOption?: (details?: { entityRef?: ModelEntityReference }) => void;
  productContext?: ProductContext;
  disableButtonClicks?: boolean;
  enableAllResults?: boolean;
  shouldHideSpecialInformationIfNotEnoughSpace?: boolean;
  // When set, company rows always show a country · listing-status tag (even
  // when a description is present) so same-named entities can be told apart.
  // Opt-in via the search box; see PLT-5578.
  showDisambiguationMetadata?: boolean;
}

export function getCustomOption(params: Params) {
  function CustomOption(props: Omit<OptionProps<ModelEntitySearchResponseItem>, 'setValue'>) {
    const {
      headerSearch,
      user,
      creatable,
      onOption,
      disableButtonClicks,
      enableAllResults,
      theme,
      shouldHideSpecialInformationIfNotEnoughSpace,
      showDisambiguationMetadata,
    } = params;

    const data = props.data;
    const focused = props.isFocused;
    const { teaser } = data;
    const teamUser = isTeamUser(user);

    const hasInvestorLicenseSubscription = useIsStandardOrExtendedCoverageUser();
    const hasAnySubscription = useIsClientOrTeamOrFreeTrial();
    const tooltip = (() => {
      if (hasInvestorLicenseSubscription) {
        return <NotCurrentlyAvailableText />;
      }
      if (hasAnySubscription) {
        return <UpgradeSubscriptionTextBrief />;
      }
      return <RestrictedContentTextBrief />;
    })();

    const isCompany = data.type === ModelEntityType.COMPANY;
    const onClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!isCompany && teaser) {
        return;
      }
      if (!disableButtonClicks && (event.metaKey || event.ctrlKey)) {
        // Cmd+click opens new tab
        event.preventDefault();
        if (isProductLike(data.type)) {
          const productUrl =
            teamUser && data.type !== ModelEntityType.MIXIN
              ? getProductViewInternalUrl(data.id, data.type, data.name, InternalProductViewMode.GRAPH_CONTEXT)
              : getProductViewUrl(data.id, data.name, data.type);
          window.open(getUiUrl() + productUrl, '_blank');
        } else if (data.type === ModelEntityType.COMPANY) {
          window.open(getUiUrl() + getCompanyViewUrl(data.id, data.name), '_blank');
        } else if (data.type === ModelEntityType.PORTFOLIO) {
          window.open(getUiUrl() + getPortfolioViewUrl(data.id, data.name), '_blank');
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        } else if (data.type === ModelEntityType.GROUP) {
          window.open(getUiUrl() + getCompanyGroupViewUrl(data.id, data.name), '_blank');
        }
        if (onOption) {
          onOption({ entityRef: { type: data.type, id: data.id } });
        }
      } else {
        if (props.innerProps.onClick) {
          props.innerProps.onClick(event);
        }
        return undefined;
      }
      return;
    };

    const isTeaserAndNotAllResultsEnabled = !!data.teaser && !enableAllResults;

    const mainElement = (
      <Main>
        <Icon type={data.type} theme={theme} grayStyle={isTeaserAndNotAllResultsEnabled} />
        <NameBlock
          item={data}
          creatable={creatable}
          grayStyle={isTeaserAndNotAllResultsEnabled}
          theme={theme}
          shouldWrap={!shouldHideSpecialInformationIfNotEnoughSpace}
        />
        {!isCompany && teaser && !enableAllResults && (
          <LockIcon
            style={{ marginLeft: '0.6em', width: '1.0em', height: '1.0em', color: BouColor.PLATFORM_ICON_GRAY }}
          />
        )}

        <DescriptionBlock item={data} />

        {showDisambiguationMetadata && isCompany ? (
          <DisambiguationBlock item={data} />
        ) : !data.description ? (
          <CountryBlock item={data} />
        ) : undefined}
      </Main>
    );

    const contentElement = shouldHideSpecialInformationIfNotEnoughSpace ? (
      <Overflower>
        <OverflowerShort aria-hidden={true}>
          <Content headerSearch={headerSearch}>{mainElement}</Content>
        </OverflowerShort>
        <OverflowerLong>
          <Content headerSearch={headerSearch}>
            {mainElement}
            <SpecialInformationBlock item={data} />
          </Content>
        </OverflowerLong>
      </Overflower>
    ) : (
      <Content headerSearch={headerSearch}>
        {mainElement}
        <SpecialInformationBlock item={data} />
      </Content>
    );

    const tooltipEnabled = (() => {
      if (enableAllResults) return false;
      if (!teaser) return false;
      if (headerSearch) return !isCompany; // In Header Search we want to pass people forward to company pages i.e. not show the tooltip
      return true;
    })();

    return (
      <Root
        theme={theme}
        focused={focused}
        onMouseOver={e => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (props.innerProps.onMouseOver) {
            props.innerProps.onMouseOver(e);
          }
        }}
        onClick={onClick}
      >
        {tooltipEnabled ? (
          <InfoTooltip tooltipContent={<div>{tooltip}</div>} trigger="click">
            {contentElement}
          </InfoTooltip>
        ) : (
          contentElement
        )}
      </Root>
    );
  }
  return CustomOption;
}

const Root = styled.div<{ theme: SearchTheme; focused: boolean }>`
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  margin: 2px 0;
  font-weight: 500;
  line-height: 1;
  background-color: ${props => {
    if (props.focused) {
      if (props.theme === 'dark') {
        return BouColor.PLATFORM_BG_GRAY;
      }
      return BouColor.PLATFORM_BG_GRAY_LIGHT;
    }
    return undefined;
  }};
`;

const Content = styled.div<Pick<Params, 'headerSearch'>>`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding-inline: ${props => (props.headerSearch ? undefined : '16px')};
`;

const Main = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 24px;
  gap: 4px;
`;

const Overflower = styled.div`
  display: flex;
  overflow: hidden;
  flex-wrap: wrap;
  width: 100%;
  height: 24px;
  line-height: 24px;
  white-space: nowrap;
`;

const OverflowerLong = styled.div`
  flex-basis: 100%;
`;

const OverflowerShort = styled.div`
  flex-grow: 1;
  width: 0;
`;

function Icon(props: { type: ModelEntityType; theme: SearchTheme; grayStyle: boolean }) {
  const color = (() => {
    if (!props.grayStyle) {
      if (props.theme === 'light') return BouColor.PLATFORM_ICON_GRAY_DARK;
      return BouColor.PLATFORM_ICON_GRAY;
    }
    return BouColor.PLATFORM_TEXT_GRAY;
  })();
  return <div style={{ color }}>{getOptionIcon(props.type)}</div>;
}
